/**
 * ensureBrandFontCoverage.ts — PLAT-11467 (epic PLAT-11407)
 *
 * The one place that decides WHEN to backfill brand-font script coverage, and
 * the only place that writes it back.
 *
 * `backfillFontScriptCoverage` does the probing; this wraps it in the real
 * fetch + parse, an at-most-once-per-workspace guard, and the persist.
 *
 * Triggered from the caption-language applier, because a non-Latin caption
 * language is precisely when coverage starts to matter — it is what
 * PLAT-11466's translated-graphics font correction and PLAT-11412's coverage
 * warnings both read. Switching to a Latin language never probes anything.
 *
 * Deliberately fire-and-forget: the language switch must not wait on font
 * downloads. When the probe lands, `onCoverageChanged` re-fires the source so
 * the composition projection re-runs with the coverage now known.
 */

import { resolveCaptionScript } from '@src/utility/captionLanguage';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { parse as parseFont } from 'opentype.js';

import { backfillFontScriptCoverage } from './backfillFontScriptCoverage';

import type { FontCoverageProbeDeps } from './backfillFontScriptCoverage';
import type { BrandingCustomFont } from '@src/types.ts/story';

const logger = LogManager.getLogger('service:BrandFontCoverage');

/**
 * Workspaces already attempted this session. Keyed on ATTEMPT, not success: a
 * workspace whose font files are unreachable must not re-download on every
 * language switch. A reload retries, which is the right cadence for a
 * one-time repair.
 */
const attempted = new Set<string>();

/** Test seam — reset the once-per-workspace guard. */
export const resetBrandFontCoverageGuard = () => attempted.clear();

const defaultProbeDeps: FontCoverageProbeDeps = {
  fetchFontBuffer: async (url: string) => {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.arrayBuffer();
  },
  parseFont: (buffer: ArrayBuffer) => parseFont(buffer),
};

export interface EnsureBrandFontCoverageParams {
  organization:
    | { id?: string; branding?: { customFonts?: BrandingCustomFont[] | null } }
    | null
    | undefined;
  /** The language just applied; Latin targets skip the whole thing. */
  targetLanguage: string | null | undefined;
  /** Persist the mutated organization (`albumRepository.update`). */
  persist: (organization: unknown) => Promise<unknown>;
  /** Re-render with the new coverage — the projection reads it on next paint. */
  onCoverageChanged?: () => void;
  probeDeps?: FontCoverageProbeDeps;
}

/**
 * Probe and persist coverage for this workspace's un-probed brand fonts.
 * Never throws and never blocks the caller: every failure path logs and
 * leaves the fonts exactly as unknown as they already were.
 */
export async function ensureBrandFontCoverage({
  organization,
  targetLanguage,
  persist,
  onCoverageChanged,
  probeDeps = defaultProbeDeps,
}: EnsureBrandFontCoverageParams): Promise<void> {
  try {
    if (!targetLanguage) return;
    // Coverage only ever changes a decision for a non-Latin script.
    if (resolveCaptionScript(targetLanguage) === 'latin') return;

    const orgId = organization?.id;
    const fonts = organization?.branding?.customFonts;
    if (!orgId || !fonts?.length) return;
    if (attempted.has(orgId)) return;
    if (fonts.every(font => font.scriptCoverage)) {
      attempted.add(orgId);
      return;
    }
    attempted.add(orgId);

    const result = await backfillFontScriptCoverage(fonts, probeDeps);
    if (!result.ok) return;
    const { fonts: next, probed, failed } = result.value;
    if (probed === 0) {
      if (failed > 0) {
        logger.warn('No brand font coverage could be recovered', { failed });
      }
      return;
    }

    // Mutate in place: `organization` is the live object the stores and the
    // Brand Kit already share by reference (same idiom as the upload path),
    // so readers see the coverage without a reload.
    if (organization?.branding) organization.branding.customFonts = next;
    await persist(organization);
    logger.info('Brand font coverage backfilled', { orgId, probed, failed });
    onCoverageChanged?.();
  } catch (error) {
    // A repair that fails leaves the workspace exactly where it was.
    logger.warn('Brand font coverage backfill failed', extractErrorInfo(error));
  }
}
